/** @jsx jsx */

import { Fragment } from 'react';

import { css, jsx } from '@emotion/core';

import ErrorPage from 'app/developer-v3/components/ErrorPage';
import { BASE_APP_URL, DEV_BREAK_POINTS } from 'app/developer-v3/constants';
import { ContactUs } from 'app/developer-v3/components/PartnerSupportForm/components/ContactUs';
import { useAvroEvent } from 'app/developer-v3/hooks';

import CTAJoinIntegration from './CTAs/CTAJoinIntegration';
import CTAStart from './CTAs/CTAStart';
import CTAWarning from './CTAs/CTAWarning';
import Integrations from './Integrations';
import Modals from './modals';
import NewsBar from './NewsBar';
import VBSketchSVG from './vb-sketch.svg';

import type { AppType } from 'app/developer-v3/types';

const Styles = {
  image: css`
    left: 50%;
    margin: 30px auto;
    position: relative;
    transform: translateX(-50%);
    max-width: 500px;
    @media screen and (max-width: ${DEV_BREAK_POINTS.default}) {
      width: 75%;
    }
  `,
  root: css`
    height: 100%;
    overflow: auto;
    padding: 24px 24px 70px;
  `,
  wrapper: css`
    margin-left: auto;
    margin-right: auto;
    max-width: 800px;
    > *:not(:first-of-type) {
      margin-top: 30px;
    }
  `,
};

type Props = {
  /* An optional integratoin ID. It's left out when a partner lands on the
   * dashboard from an invite link. */
  integrationId?: string;
  apps: Array<AppType>;
  hasAcceptedTerms: boolean;
  hasFailure: boolean;
  hasPartnerProfile: boolean;
  isAdminMasqueraded: boolean;
  isDeveloper: boolean;
  isLoadingPartnerProfile: boolean;
  onViewVersion: (args: {
    id: number;
    isBeta?: boolean;
    isPublic?: boolean;
    version?: string;
  }) => void;
  onViewVersions: (args: { id: number }) => void;
};

const defaultProps = {
  apps: [],
};

function HomePage(props: Props) {
  const {
    integrationId,
    apps,
    hasAcceptedTerms,
    hasFailure,
    hasPartnerProfile,
    isAdminMasqueraded,
    isDeveloper,
    isLoadingPartnerProfile,
    onViewVersion,
    onViewVersions,
  } = props;

  const { emitUserInteractionEvent } = useAvroEvent();

  // These flags are intentionally made flattened with AND operator. Please
  // AVOID creating dependency between them. It'll be bug-prone because of
  // greater complexity and impaired readability.
  //
  // TODO: Move these modals to Developer or AppBuilder. They ask for agreement
  // to platform terms, which is a requirement for all pages.
  const shouldShowInviteModal = !!integrationId;
  const shouldShowOnboardingModalNewDeveloper =
    !isAdminMasqueraded && !integrationId && !isDeveloper && !hasAcceptedTerms;
  const shouldShowOnboardingModalExistingDeveloper =
    !isAdminMasqueraded &&
    isDeveloper &&
    !isLoadingPartnerProfile &&
    (!hasPartnerProfile || !hasAcceptedTerms);

  const shouldShowCTAStart = !integrationId && !apps.length && hasAcceptedTerms;
  const shouldShowCTAWarning =
    !integrationId && !!apps.length && hasAcceptedTerms;
  const shouldShowCTAJoinIntegration = !integrationId && hasAcceptedTerms;

  const emitIntegrationOpenedEvent = (title: string) => {
    emitUserInteractionEvent({
      eventAction: 'click',
      interactionGoal: 'View integration page',
      interactionName: 'integration_selected',
      interactionRawText: title,
      optionSelected: '',
    });
  };

  return (
    <div css={Styles.root}>
      <div css={Styles.wrapper}>
        {hasFailure ? (
          <ErrorPage />
        ) : (
          <Fragment>
            <NewsBar />
            {shouldShowInviteModal && (
              <Modals.Invite appId={integrationId} isCLIIntegration={true} />
            )}
            {shouldShowOnboardingModalNewDeveloper && <Modals.OnboardNewDev />}
            {shouldShowOnboardingModalExistingDeveloper && (
              <Modals.OnboardExistingDev />
            )}
            {shouldShowCTAStart && (
              <CTAStart createAppUrl={`${BASE_APP_URL}/app/new`} />
            )}
            {shouldShowCTAWarning && (
              <CTAWarning createAppUrl={`${BASE_APP_URL}/app/new`} />
            )}
            {shouldShowCTAJoinIntegration && <CTAJoinIntegration />}
            {apps.length > 0 && (
              <Integrations
                integrations={apps}
                onViewVersion={({ id, isBeta, isPublic, title, version }) => {
                  emitIntegrationOpenedEvent(title);
                  onViewVersion({ id, isBeta, isPublic, version });
                }}
                onViewVersions={({ id, title }) => {
                  emitIntegrationOpenedEvent(title);
                  onViewVersions({ id });
                }}
              />
            )}
            <ContactUs />
            {apps.length === 0 && (
              <VBSketchSVG alt="Take a Tour" css={Styles.image} />
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
}

HomePage.defaultProps = defaultProps;

export default HomePage;
